import React, { useEffect, useState, useRef } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import AlarmServicesTable from "./AlarmServicesTable"
import AlarmFilter from "components/Filters/AlarmFilter"
import CustomRangePicker from "components/DatePickers/RangePicker"
import {
  ackAlarms,
  deleteSelectedAlarms,
  getAlarms,
  resolveAlarms,
} from "helpers/backend_helper"
import {
  Card,
  CardBody,
  Col,
  Row,
  Form,
  Label,
  FormGroup,
  Button,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  UncontrolledTooltip,
  Collapse,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import { Switch, Input as AntdInput } from "antd"
import dayjs from "dayjs"
import PubSub from "pubsub-js"
import { Link } from "react-router-dom"
import { showToast, downloadExcel, dateFormatter, isTrue } from "utils/general"
const { TextArea } = AntdInput;


const AlarmServices = props => {
  const abortRef = useRef(null)
  const [entries, setEntries] = useState([])
  const [filteredEntries, setFilteredEntries] = useState([])
  const [filteredEntriesAll, setFilteredEntriesAll] = useState([])
  const [showAll, setShowAll] = useState(false)
  const [showAutoClosed, setShowAutoClosed] = useState(true)
  const [selectedAlarms, setSelectedAlarms] = useState([])
  const [selectedAlarmsObj, setSelectedAlarmsObj] = useState([])
  const [closeModal, setCloseModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [comments, setComments] = useState("")
  const [falseAlarm, setFalseAlarm] = useState(false)
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, "months"),
    dayjs(),
  ])
  const [dateSelected, setDateSelected] = useState(false)
  const [menu, setMenu] = useState(false) //Dropdown button state
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    document.title = props.t("Customer management") + " | Suvanto Care"
    abortRef.current = new AbortController()
    const token = PubSub.subscribe("page-refresh", (event, data) => {
      setDateRange(prevState => [prevState[0], dayjs()])
      setDateSelected(false)
    })
    return () => {
      abortRef.current.abort()
      PubSub.unsubscribe(token)
    }
  }, [])

  useEffect(() => {
    if (dateRange && props.suvantoKey) getAlarmsList(true, !dateSelected)
  }, [showAll, showAutoClosed, props.suvantoKey])

  // Update selected objects & alarmkeys
  useEffect(() => {
    setIsLoading(true)
    if (showAutoClosed) {
      setFilteredEntriesAll(filteredEntries)
    } else {
      let filtered = []
      filtered = filteredEntries.filter(
        f => f.auto_closed == undefined || !f.auto_closed
      )
      setFilteredEntriesAll(filtered)
    }
    setIsLoading(false)
  }, [filteredEntries])

  // get alarms
  useEffect(() => {
    if (!isLoading) {
      const interval = setInterval(() => {
        getAlarmsList(true, !dateSelected) // Get without loading animation
      }, 30000)
      return () => clearInterval(interval)
    }
  }, [isLoading, dateSelected, showAll])

  function setTranslatedExtraInfo(raw) {
    const input = raw == null ? "" : String(raw)
    const parts = input.split("/")
    const leftRaw = parts.shift()
    const rightRaw = parts.join("/")

    const leftText = (leftRaw == null ? "" : leftRaw).trim()
    const rightText = (rightRaw == null ? "" : rightRaw).trim()

    const tr = typeof props?.t === "function" ? props.t(leftText) : leftText
    const left = typeof tr === "string" && tr.trim() !== "" ? tr : leftText

    return rightText ? left + " / " + rightText : left
  }

  // Fetch alarms
  const getAlarmsList = (noLoadingAnim, refreshTime) => {
    if (dateRange && props.suvantoKey) {
      if (!noLoadingAnim) {
        setIsLoading(true)
      }

      let endTime = dateRange[1]
      // Set end time to current
      if (refreshTime) {
        endTime = dayjs()
        setDateRange(prevState => [prevState[0], endTime])
      }

      setSelectedAlarmsObj([])
      setSelectedAlarms([])
      getAlarms(
        abortRef.current,
        props.suvantoKey,
        dateRange[0].valueOf(),
        endTime.valueOf(),
        showAll
      )
        .then(data => {
          for (const element of data.entries) {
            // Map link
            if (element.location?.latitude) {
              element.map_link =
                "https://www.google.com/maps/place/" +
                element.location.latitude +
                "," +
                element.location.longitude
            }

            element.extra_info = ""
            switch (element.alarm_id) {
              case 1:
                if (element.value) element.extra_info = element.value + "%"
                break
              case 13:
                if (element.node_name && element.value)
                  element.extra_info =
                    element.node_name + " (" + element.value + ")"
                break
              case 15:
              case 16:
                if (element.value)
                  element.extra_info =
                    props.t("over") + " " + element.value + "min"
                break
              case 18:
              case 19:
                if (element.node_name && element.value)
                  element.extra_info =
                    element.node_name + " " + element.value + "°C"
                break
              case 20:
              case 21:
                if (element.node_name && element.value)
                  element.extra_info =
                    element.node_name + " " + element.value + "%"
                break
              case 17:
              case 22:
              case 23:
                if (element.node_name)
                  element.extra_info = "" + element.node_name
                break
              case 27:
              case 36: {
                // Carephone portable trigger
                element.extra_info = setTranslatedExtraInfo(
                  element.string_value
                )
                break
              }
              case 37: // Carephone smoke alarm
              case 38: // Carephone door alarm
              case 42: // Carephone fall
              case 47: {
                // Carephone accessory battery alarm
                element.extra_info = setTranslatedExtraInfo(
                  element.string_value
                )
                break
              }
              case 49: // Carephone CO gas
                if (element.string_value)
                  element.extra_info = "" + element.string_value
                break
              case 30: // Abnormal measurement
                if (element.string_value && element.value)
                  element.extra_info =
                    props.t(element.string_value) + " " + element.value
                break
              case 31: // Measurement missed
                if (element.string_value)
                  element.extra_info = props.t(element.string_value)
                break
            }
          }
          //console.log(data.entries)
          setEntries(data.entries)
        })
        .catch(err => {
          console.log(err)
        })
        .finally(() => {
          if (!noLoadingAnim) setIsLoading(false)
        })
    }
  }

  // Ack selected alarms
  const ackSelectedAlarms = () => {
    if (selectedAlarms.length > 0) {
      ackAlarms(abortRef.current, selectedAlarms)
        .then(res => {
          getAlarmsList(true, !dateSelected)
        })
        .catch(err => {
          console.log(err)
        })
    } else console.log("not selected")
  }

  // Close selected alarms
  const resolveSelectedAlarms = () => {
    if (selectedAlarms.length > 0) {
      resolveAlarms(abortRef.current, selectedAlarms, comments, falseAlarm)
        .then(res => {
          setComments("")
          setFalseAlarm(false)
          getAlarmsList(true, !dateSelected)
          setCloseModal(false)
        })
        .catch(err => {
          console.log(err)
        })
    }
  }

  // Delete selected alarms
  const deleteSelections = () => {
    if (selectedAlarms.length > 0) {
      deleteSelectedAlarms(abortRef.current, props.suvantoKey, selectedAlarms)
        .then(result => {
          console.log("Delete alarm result: ", result)
          if (result.statusCode == 200) {
            getAlarmsList(true, !dateSelected)
            setDeleteModal(false)
            showToast(props.t("Deleted"), "success")
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
    }
  }

  // Export values
  const generateExcel = () => {
    const heading = [
      [
        props.t("Time"),
        props.t("Type"),
        props.t("Info"),
        props.t("False alarm"),
        props.t("Comments"),
        props.t("Auto closed"),
        props.t("Handler"),
        props.t("Handling time"),
        props.t("Closed by"),
        props.t("Closed at"),
        props.t("Location"),
      ],
    ]
    const data = filteredEntriesAll.map(elt => [
      dateFormatter(elt.received_at),
      props.t("AlarmTitles." + elt.alarm_id),
      elt.extra_info,
      isTrue(elt.false_alarm),
      elt.comment,
      isTrue(elt.auto_closed),
      elt.acked_by,
      dateFormatter(elt.acked_at),
      elt.closed_by,
      dateFormatter(elt.closed_at),
      elt.map_link,
    ])

    downloadExcel(props.t("Alarms"), heading, data)
    showToast(props.t("Excel file exported succesfully!"), "success", 5000, "toast-bottom-left")
  }

  const toggleShow = () => {
    setShowAll(prevState => !prevState)
  }
  const toggleShowAutoClosed = () => {
    setShowAutoClosed(prevState => !prevState)
  }

  return (
    <>
      <Card className="mt-0">
        <CardBody>
          <Row>
            <Col className="mb-1" style={{ minWidth: "350px" }}>
              <AlarmFilter
                data={entries}
                setFilteredEntries={setFilteredEntries}
                needsAlarmType={true}
              />
            </Col>
            <Col className="mb-1" style={{ minWidth: "350px" }}>
              <CustomRangePicker
                value={dateRange}
                rangeChanged={range => {
                  setDateRange(range)
                  setDateSelected(true)
                }}
                onOk={() => {
                  getAlarmsList(true, false)
                }}
                showTime={true}
                allowClear={false}
                format={"DD.MM.YYYY HH:mm"}
              />
            </Col>
            <Col className="col-auto">
              <span>
                <div className="mt-1 mb-1">
                  <Switch
                    className="me-2"
                    name="toggleShow"
                    id="toggleShow"
                    checked={showAll}
                    onChange={toggleShow}
                    checkedChildren={
                      <div className="d-flex" style={{ maxHeight: "22px" }}>
                        <i className="mdi mdi-check-circle-outline font-size-18" />
                      </div>
                    }
                    unCheckedChildren={
                      <div className="d-flex" style={{ maxHeight: "22px" }}>
                        <i className="mdi mdi-check-circle-outline font-size-18" />
                      </div>
                    }
                  ></Switch>
                  <UncontrolledTooltip placement="right" target="toggleShow">
                    {props.t("Show closed alarms")}
                  </UncontrolledTooltip>
                  <Switch
                    className="me-2"
                    name="toggleShow"
                    id="toggleShowAuto"
                    checked={showAutoClosed}
                    onChange={toggleShowAutoClosed}
                    disabled={!showAll}
                    checkedChildren={
                      <div className="d-flex" style={{ maxHeight: "22px" }}>
                        <i className="mdi mdi-spellcheck font-size-18" />
                      </div>
                    }
                    unCheckedChildren={
                      <div className="d-flex" style={{ maxHeight: "22px" }}>
                        <i className="mdi mdi-spellcheck font-size-18" />
                      </div>
                    }
                  ></Switch>
                  <UncontrolledTooltip
                    placement="right"
                    target="toggleShowAuto"
                  >
                    {props.t("Show automatically closed alarms")}
                  </UncontrolledTooltip>
                </div>
              </span>
            </Col>
            <Col>
              <span className="float-end ms-2">
                <UncontrolledTooltip
                  placement="bottom"
                  target="downloadstatstable"
                >
                  {props.t("Download statistics")}
                </UncontrolledTooltip>
                <Dropdown
                  id="printButtonServicesStats"
                  isOpen={menu}
                  toggle={() => setMenu(!menu)}
                  className="download-btn text-center"
                  type="button"
                >
                  <DropdownToggle
                    id="downloadstatstable"
                    className="text-muted"
                    tag="i"
                  >
                    <i className="mdi mdi-download" />
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem
                      onClick={() => generateExcel()}
                    >
                      {props.t("Download .xlsx")}
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </span>
            </Col>
          </Row>
          <Collapse isOpen={selectedAlarmsObj.length != 0}>
            <Row>
              <Col className="py-2">
                {selectedAlarmsObj.some(
                  obj =>
                    obj.status === "UNASSIGNED" || obj.status === "ASSIGNED"
                ) && (
                    <>
                      <Button
                        outline
                        color="primary"
                        className="me-3"
                        onClick={() => {
                          ackSelectedAlarms()
                        }}
                      >
                        <i className="mdi mdi-alert-circle me-2" />
                        {props.t("Ack selected alarms")}
                      </Button>
                      <Button
                        color="primary"
                        className="me-3"
                        onClick={() => {
                          setCloseModal(true)
                        }}
                      >
                        <i className="bx bx-check-circle me-2" />
                        {props.t("Close selected alarms")}
                      </Button>
                    </>
                  )}
                {selectedAlarmsObj.length > 0 && (
                  <Button
                    color="danger"
                    className="me-3"
                    onClick={() => {
                      setDeleteModal(true)
                    }}
                  >
                    <i className="mdi mdi-trash-can me-2" />
                    {props.t("Delete selected alarms")}
                  </Button>
                )}

                <span className="text-muted">
                  {selectedAlarmsObj.length} {props.t("selected alarms")}
                </span>
              </Col>
            </Row>
          </Collapse>

          {isLoading && entries.length == 0 ? (
            <Row>
              <Col xs="12">
                <div className="text-center my-3">
                  <Link to="#" className="text-primary py-4">
                    <i className="bx bx-loader bx-spin font-size-18 align-middle me-2" />
                    {props.t("Loading")}
                  </Link>
                </div>
              </Col>
            </Row>
          ) : (
            <div className="mt-3">
              {entries.length === 0 || filteredEntries.length === 0 ? (
                <div className="text-center my-3">
                  {!showAll ? (
                    <h5 to="#" className="py-5 text-success">
                      <i className="bx bx-badge-check font-size-18 align-middle me-2" />
                      {props.t("No unresolved alarms")}
                    </h5>
                  ) : (
                    <h5 to="#" className="py-4 ">
                      {props.t("No alarms found")}
                    </h5>
                  )}
                </div>
              ) : (
                <AlarmServicesTable
                  loading={isLoading}
                  data={filteredEntriesAll}
                  selectedAlarms={selectedAlarms}
                  setSelectedAlarms={setSelectedAlarms}
                  setSelectedAlarmsObj={setSelectedAlarmsObj}
                ></AlarmServicesTable>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={closeModal}
        toggle={() => {
          setOpenRequestModal(!closeModal)
        }}
        centered
      >
        <ModalHeader>{props.t("Close selected alarms")}</ModalHeader>
        <ModalBody>
          <Form className="form-horizontal">
            <FormGroup>
              <Label>{props.t("Comments")}</Label>
              <TextArea
                allowClear
                showCount
                maxLength={500}
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder={props.t("Optional")}
                value={comments}
                onChange={(v) => { setComments(v.target.value) }}
              />
            </FormGroup>
            <FormGroup>
              <Switch
                className="me-2"
                name="falseAlarm"
                checked={falseAlarm}
                onChange={val => {
                  setFalseAlarm(val)
                }}
              ></Switch>
              <Label>{props.t("False alarm")}</Label>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              resolveSelectedAlarms()
            }}
          >
            {props.t("Close alarms")}
          </Button>
          <Button
            color="primary"
            outline
            onClick={() => {
              setCloseModal(false)
            }}
          >
            {props.t("Cancel")}
          </Button>
        </ModalFooter>
      </Modal>

      {/** Delete alarms modal */}
      <Modal
        isOpen={deleteModal}
        toggle={() => {
          setDeleteModal(!deleteModal)
        }}
        centered
      >
        <ModalHeader>{props.t("Delete selected alarms")}</ModalHeader>
        <ModalBody>
          <p>{props.t("Are you sure you want to delete selected alarms?")}</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            onClick={() => {
              setDeleteModal(false)
            }}
          >
            {props.t("Cancel")}
          </Button>
          <Button
            color="danger"
            onClick={() => {
              deleteSelections()
            }}
          >
            {props.t("Delete")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

AlarmServices.propTypes = {
  t: PropTypes.any,
  suvantoKey: PropTypes.any,
}

export default withTranslation()(AlarmServices)
